import { ApiResponse } from './api-types';
import { Shop } from './edge-api-types';
import { CompanyStatusType } from './customs';
import { DutyCalculation, DutyType } from './duty-types';
import { ReactNode } from 'react';

export type NewBasketOptions = {
  origin: string;
  originPostcode?: string;
  destination?: string;
  destinationPostcode?: string;
  weight: number;
  height?: number;
  length?: number;
  width?: number;
  value?: number;
};

export type Basket = {
  id: string;
  items: { [name: string]: BasketItem };
};

export type BasketItem = {
  sections: { [name: string]: BasketSection };
};

export type BasketSection = {
  version?: string | null | undefined;
  payload: unknown;
  check?: number | null | undefined;
};

export type Quote = {
  $type: 'shipment';
  dates: Array<DateElement>;
  restrictions: Restrictions;
  service: Service;
  id: string;
  ref: string;
  price: QuotePrice;
  availableExtras: Array<AvailableExtra>;
  tags: Array<string>;
  metadata:
    | {
        estimatedDeliveryDaysMax?: number;
        estimatedDeliveryDaysMin?: number;
        [name: string]: number | string | Array<string> | boolean | undefined;
      }
    | undefined;
};

export type BasketQuote = Quote & {
  currency: Currency;
  errors?: QuoteError[];
  etag?: string | null;
};

export interface AvailableExtra {
  key: string;
  price: Price;
  name: string;
  tags: string[];
  metadata:
    | {
        protectedAmount?: number;
        maxProtection?: number;
        requires?: string[];
        defaultSelected?: boolean;
        [name: string]: number | string | string[] | boolean | undefined;
      }
    | undefined;
  requirements?: QuoteRequirement[];
}

export interface Price {
  gross: number;
  net: number;
  tax: number;
  discount?: number;
  breakdown?: PriceBreakdown[];
}

export interface QuotePrice extends Price {
  breakdown: PriceBreakdown[];
}

export interface PriceBreakdown {
  amount: number;
  name: string;
  metadata:
    | {
        parcelId?: number | undefined;
        surcharge?: 'out-of-gauge' | 'remote-destination' | 'local-tax' | undefined;
        [name: string]: string | number | boolean | undefined;
      }
    | undefined;
}

export enum DateType {
  Collection = 'COLLECTION',
  Dropoff = 'DROPOFF'
}

export interface DateElement {
  collection?: CollectionDate;
  cutoff?: string;
  delivery: DeliveryDate;
  extraKey?: string;
  type: DateType;
  price?: Price;
}

export interface Restrictions {
  maxWeight: number;
  maxHeight?: number;
  maxWidth?: number;
  maxLength?: number;
  minWeight?: number;
  maxVolumetricWeight?: number;
  maxLengthPlusGirth?: number;
  maxVolume?: number;
}

export interface DeliveryDate {
  dateMin: string;
  dateMax: string;
  timeMin: string;
  timeMax: string;
}

export interface CollectionDate {
  date: string;
  timeMin: string;
  timeMax: string;
}

export interface Service {
  slug: string;
  name: string;
  dropOffProviderGroup: string;
  courier: string;
  courierSlug: string;
  includedProtection?: number;
  maxProtection?: number;
}

export interface Currency {
  code: string;
  symbol: string;
}

export interface QuoteCountryRedirect {
  iso2Code: string;
  iso3Code: string;
  postcodeRegex: string;
}

export interface QuoteCountry {
  name: string;
  iso2Code: string;
  iso3Code: string;
  metadata: {
    group: string;
    class: string;
    subClass: string;
    quoteRequiresPostcode: boolean;
    postcodeValidator?: string;
    postcodeAutocomplete?: boolean;
    position?: number;
    dimensions: 'optional' | 'recommended';
  };
  redirects?: QuoteCountryRedirect[];
}

export interface QuoteRequirements {
  [name: string]: QuoteRequirementSection;
}

export interface QuoteRequirementSection {
  displayName: string;
  key: string;
  required: boolean;
  rules: QuoteRequirement[];
  metadata: {
    min?: number;
    max?: number;
    array?: boolean;
    forEach?: string;
    [name: string]: string | number | boolean | undefined;
  };
}

export type BasketRequirementExplicit = {
  field: string;
  value: string | string[];
};

export type BasketRequirementMultiple = {
  all?: { field: string; value?: string[] }[];
  any?: { field: string; value?: string[] }[];
  value?: string[];
};

export type OptionType = {
  value: string;
  label: string;
};

export interface QuoteRequirement {
  key: string;
  name: string;
  required: boolean;
  valueType: 'select' | 'string' | 'decimal' | 'integer';
  metadata: {
    minValue?: number;
    maxValue?: number;
    placeholder?: string;
    requiredMessage?: string;
    minLength?: number;
    maxLength?: number;
    regex?: string;
    example?: string;
    default?: string | number;
    options?: string[] | OptionType[];
    format?: string;
    type?: string;
    unit?: string;
    helperText?: ReactNode;
    [name: string]:
      | string
      | number
      | boolean
      | string[]
      | undefined
      | BasketRequirementExplicit
      | BasketRequirementMultiple
      | OptionType[]
      | ReactNode;
    maxParcelValue?: number;
    requiredIf?: BasketRequirementExplicit | BasketRequirementMultiple;
  };
}

export type QuoteAddress = {
  contactname?: string;
  country: string;
  postcode?: string;
  town?: string;
  street?: string;
  property?: string;
  county?: string;
  locality?: string;
  organisation?: string;
  phone?: string;
  email?: string;
  shop?: Shop;
  shopId?: number;

  companyStatus?: CompanyStatusType;
  taxId?: string;
  eori?: string;
  companyTaxNumber?: string;

  metadata?: Record<string, string | number | boolean | undefined> & {
    sendToMultiple?: 'split-same-contents' | 'split-different-contents' | undefined;
  };
  [name: string]: string | Shop | undefined | number | Record<string, string | number | boolean | undefined>;
};

export type CommunicationPreferences = {
  [name: string]: boolean;
};

export type QuoteParcels = {
  parcels: QuoteParcel[];
  initialParcels?: number;
  splitParcels: QuoteParcel[];
};

export type QuoteParcel = {
  id: string;
  weight: number;
  quantity: number;
  value?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  contentSummary?: string;
  category?: string;
  contents: QuoteParcelContents[];
};

export type QuoteParcelContents = {
  id: string;
  quantity?: number;
  description?: string;
  value?: number | null;
  countryOfManufacture?: string;
  tariffCode?: string;
  tariffCodeDescription?: string;
};

export type QuoteExtras = {
  extras: QuoteExtra[];
  initialExtras: QuoteExtra[];
  userInteractedWithProtection: boolean;
};

export type QuoteExtrasCalculations = {
  ddp?: DutyCalculation;
};

export type QuoteExtra = {
  key: string;
  metadata: { [name: string]: string | number | boolean } & { requires?: string[] };
};

export type QuoteDates = {
  dates: QuoteDate[];
};

export type QuoteDate = {
  type: string;
  date: string;
  metadata: { [name: string]: string | number | boolean };
};

export type BasketMeta = {
  staging?: boolean;
  completed: boolean;
  created: string;
  metadata: { [name: string]: string };
  [name: string]: number | string | string[] | boolean | undefined | object;
};

export type Account = {
  forename?: string;
  surname?: string;
  email?: string;
  mobile?: string;
};

export type Legal = {
  termsAccepted?: boolean;
  serviceTermsAccepted?: { [name: string]: boolean };
};

export type Customs = {
  exportReason?: string;
  ioss?: string;
  soldOn?: string | undefined | '';
  dutyType?: DutyType;
};

export type Order = {
  orderId: string;
  accountId: string;
  hash: string;
  ref: string;
  orderLines: { ref: string | undefined; orderLineId: string }[];
  total: Price;
};

export type QuoteError = {
  type: string;
  title: string;
  detail: string;
  field?: string;
  itemId?: string;
  itemRef?: string | null;
  section?: string;
};

export type GenericErrorResponse = {
  type: string;
  title: string;
  status: number;
  detail: string;
  issues: QuoteError[];
};

export type QuoteProtection = {
  loading: boolean;
  requestId?: string | undefined;
  includedProtection?: number | undefined;
  options: ProtectionOption[];
};
export type ProtectionOption = {
  key: string;
  price: Price;
  name: string;
  tags: string[];
  metadata:
    | {
        protectedAmount?: number;
        maxProtection?: number;
        [name: string]: number | string | string[] | boolean | undefined;
      }
    | undefined;
};

export type OrderItemValidation = {
  id: string;
  availableExtras: AvailableExtra[];
};

export type OrderValidationBreakdown = { amount: number; name: string; metadata: { parcelId: string } }[];

export type OrderValidationTotal = Price & {
  breakdown: OrderValidationBreakdown;
};

export type OrderValidation = {
  total: OrderValidationTotal;
  ref: string;
  items: OrderItemValidation[];
};

export type DiscountCode = {
  code?: string;
};

export const ForceMaximumRequirementsLength: { [name: string]: number } = {
  contactname: 35,
  organisation: 35,
  property: 35,
  street: 35,
  locality: 35,
  town: 35,
  county: 35,
  postcode: 10,
  email: 256,
  phone: 24
};

export type ValidationResponse = ApiResponse<OrderValidation>;
export type OrderResponse = ApiResponse<Order>;
export type BasketResponse = ApiResponse<Basket>;
export type QuoteResponse = ApiResponse<Quote[]> & { currency: Currency };
export type CountryResponse = ApiResponse<{ countries: QuoteCountry[] }>;
export type QuoteRequirementResponse = ApiResponse<{ ruleSets: QuoteRequirements }>;
